import { useCallback, useEffect, useState } from "react";
import { DemoAirline } from "../config/movmoIdentity";
import {
    DemoAirlineLoyaltyClaims,
    DemoAirlinePreferenceClaims,
    DemoAirlineProfileClaims,
} from "../services/demoAirlineApi";
import { deriveWireLoyaltyLink } from "../services/mockLoyalty";

export interface LinkedTraveler {
    // The demo session's display name (GET /me `traveler.display_name`). A
    // generated demo identity: kept for the session, never used as a greeting.
    display_name?: string;
    profile?: DemoAirlineProfileClaims;
    preferences?: DemoAirlinePreferenceClaims;
    // MOVMO-567: the loyalty claim group from the same /me response,
    // present only when the traveler granted loyalty.read to this airline in
    // Movmo. Wire-authoritative — see hooks/useLoyaltySession.ts.
    loyalty?: DemoAirlineLoyaltyClaims;
}

const STORAGE_PREFIX = "movmo_linked_traveler_";

// Broadcast channel between hook INSTANCES on the same page: the identity
// chip's useLinkedTraveler instance writes the profile read from the airline
// session (GET /me), but consumers (e.g. AeroHeader's greeting) hold their
// OWN useState: without an event they'd only pick up the change on their
// next mount.
const SYNC_EVENT = "movmo:linked-traveler";

/**
 * The name for the linked greeting: the Movmo profile first name, else null
 * (the chip then greets without a name). Never falls back to the session
 * `display_name`: that is the generated demo identity (`demo-traveler+<id>`),
 * not something the traveler gave us.
 */
export function greetingName(traveler: LinkedTraveler | null): string | null {
    return traveler?.profile?.first_name?.trim() || null;
}

/** The `traveler` prop of `@movmo/connect-button`'s single chip. */
export type MovmoTraveler = { firstName?: string } | null | undefined;

/**
 * Derives the chip's `traveler` from the airline session read: `undefined`
 * while the /me read is pending, `null` when nobody is linked, else
 * `{ firstName }` (omitted when the profile has none).
 */
export function toMovmoTraveler(
    traveler: LinkedTraveler | null,
    checked: boolean,
): MovmoTraveler {
    if (!checked) return undefined;
    if (!traveler) return null;
    return { firstName: greetingName(traveler) ?? undefined };
}

function storageKey(airline: DemoAirline): string {
    return `${STORAGE_PREFIX}${airline}`;
}

function broadcast(airline: DemoAirline): void {
    window.dispatchEvent(
        new CustomEvent(SYNC_EVENT, { detail: { airline } }),
    );
}

function read(airline: DemoAirline): LinkedTraveler | null {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(storageKey(airline));
    if (!raw) return null;
    try {
        return JSON.parse(raw) as LinkedTraveler;
    } catch {
        return null;
    }
}

/**
 * MOVMO-542 (notice-race fix): resolves once `airline`'s traveler in
 * sessionStorage carries a non-null `loyalty` group, or after `timeoutMs`,
 * whichever comes first. A caller that needs to know whether a loyalty GRANT
 * landed (not just whether the session read settled) must not resolve on a
 * stale pre-grant snapshot: this keeps listening past an existing but
 * loyalty-less snapshot, re-checking storage on every SYNC_EVENT broadcast
 * (useLinkedTraveler's /me read, or a loyalty-link response), until loyalty
 * shows up or the timeout elapses. At timeout it resolves with whatever is
 * stored (loyalty present or not; the caller decides what that means).
 */
export function waitForLoyaltyGrant(
    airline: DemoAirline,
    timeoutMs: number,
): Promise<LinkedTraveler | null> {
    // Same rule deriveWireLoyaltyLink applies everywhere else a "does the
    // traveler have a usable loyalty link" decision is made (MOVMO-567):
    // a `loyalty` group with zero programs doesn't count. Using the same
    // predicate here avoids stopping the wait early on a transient/empty
    // payload that the caller's own deriveWireLoyaltyLink call would just
    // treat as absent anyway.
    const hasLoyalty = (t: LinkedTraveler | null) =>
        deriveWireLoyaltyLink(t?.loyalty) !== null;
    const existing = read(airline);
    if (hasLoyalty(existing)) return Promise.resolve(existing);
    return new Promise((resolve) => {
        const finish = () => {
            window.clearTimeout(timer);
            window.removeEventListener(SYNC_EVENT, onSync);
            resolve(read(airline));
        };
        const onSync = (event: Event) => {
            const detail = (event as CustomEvent<{ airline?: DemoAirline }>)
                .detail;
            if (detail?.airline && detail.airline !== airline) return;
            if (hasLoyalty(read(airline))) finish();
        };
        const timer = window.setTimeout(finish, timeoutMs);
        window.addEventListener(SYNC_EVENT, onSync);
    });
}

/**
 * sessionStorage-backed hook holding the traveler profile/preferences read
 * from the airline session (demoAirlineApi.getMeWithCachedSession, written by
 * useLinkedTraveler), namespaced per airline (mirrors useMockMode.ts's
 * sessionStorage pattern). Cleared when the airline session carries no
 * linked Movmo identity.
 */
export function useLinkedTravelerState(airline: DemoAirline) {
    const [traveler, setTravelerState] = useState<LinkedTraveler | null>(
        () => read(airline),
    );

    // Re-sync on mount/airline change, AND whenever any other hook instance
    // on this page broadcasts an update (SYNC_EVENT) — this is what lets the
    // header greeting appear the moment the session read completes, instead of
    // on the next navigation.
    useEffect(() => {
        setTravelerState(read(airline));
        const onSync = (event: Event) => {
            const detail = (event as CustomEvent<{ airline?: DemoAirline }>)
                .detail;
            if (!detail?.airline || detail.airline === airline) {
                setTravelerState(read(airline));
            }
        };
        window.addEventListener(SYNC_EVENT, onSync);
        return () => window.removeEventListener(SYNC_EVENT, onSync);
    }, [airline]);

    const setTraveler = useCallback(
        (next: LinkedTraveler | null) => {
            if (next) {
                window.sessionStorage.setItem(
                    storageKey(airline),
                    JSON.stringify(next),
                );
            } else {
                window.sessionStorage.removeItem(storageKey(airline));
            }
            setTravelerState(next);
            broadcast(airline);
        },
        [airline],
    );

    const clearTraveler = useCallback(() => {
        window.sessionStorage.removeItem(storageKey(airline));
        setTravelerState(null);
        broadcast(airline);
    }, [airline]);

    return { traveler, setTraveler, clearTraveler };
}

/**
 * MOVMO-542: applies a fresh wire-legit traveler snapshot from a
 * loyalty-link/unlink response's `movmo` payload (demoAirlineApi.ts,
 * DemoAirlineLoyaltyLinkResponse) into the SAME store the /me read feeds.
 * Both are server-side resolve results with identical
 * {recognized, profile?, preferences?, loyalty?} shapes, so this is a
 * legitimate wire source, not a client-side fabrication. Callable outside a
 * component so LoyaltyLinkModal/LoyaltyMemberChip can push a refresh the
 * moment their API call resolves; the broadcast makes every mounted
 * useLinkedTravelerState instance (header, chip, useLoyaltySession) pick it
 * up immediately.
 *
 * `movmo.recognized === false` (the post-link refresh 404d, consent revoked
 * mid-flight) clears the stored traveler; the link/unlink operation itself
 * already succeeded upstream by that point.
 */
export function applyWireTraveler(
    airline: DemoAirline,
    movmo: {
        recognized: boolean;
        profile?: DemoAirlineProfileClaims;
        preferences?: DemoAirlinePreferenceClaims;
        loyalty?: DemoAirlineLoyaltyClaims;
    },
): void {
    if (!movmo.recognized) {
        window.sessionStorage.removeItem(storageKey(airline));
        broadcast(airline);
        return;
    }
    const next: LinkedTraveler = {
        display_name: read(airline)?.display_name,
        profile: movmo.profile,
        preferences: movmo.preferences,
        loyalty: movmo.loyalty,
    };
    window.sessionStorage.setItem(storageKey(airline), JSON.stringify(next));
    broadcast(airline);
}
